import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  CollaborationMetricsResponse,
  WorkflowInfoMetricsResponse,
} from '@/types/composer-dashboard';
import { Skeleton } from '@/components/ui/skeleton';
import { MetricCard } from './metric-card';
import { SectionHeading } from './section-heading';

interface WorkflowInfoSectionProps {
  workflowInfo: WorkflowInfoMetricsResponse | undefined;
  collaboration: CollaborationMetricsResponse | undefined;
  isWorkflowInfoLoading?: boolean;
  isCollaborationLoading?: boolean;
  error?: Error | null;
}

export function WorkflowInfoSection({
  workflowInfo,
  collaboration,
  isWorkflowInfoLoading = false,
  isCollaborationLoading = false,
  error,
}: WorkflowInfoSectionProps) {
  const isLoading = isWorkflowInfoLoading || isCollaborationLoading;

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-64' />
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
          {Array.from({ length: 3 }).map((_, idx) => (
            <Card key={idx}>
              <CardHeader className='pb-4'>
                <Skeleton className='h-6 w-32' />
              </CardHeader>
              <CardContent>
                <Skeleton className='h-[200px] w-full' />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || (!workflowInfo && !collaboration)) {
    return (
      <div className='space-y-4'>
        <SectionHeading>Workflow Information</SectionHeading>
        <div className='flex h-[120px] items-center justify-center text-muted-foreground'>
          <p className='text-sm'>
            {error?.message || 'No workflow data available'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <SectionHeading>Workflow Information</SectionHeading>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
        <MetricCard
          title='New Workflows'
          metrics={[
            {
              label: 'Last 7 Days',
              value: workflowInfo?.new_workflows_7d?.toLocaleString() ?? '0',
            },
            {
              label: 'Total',
              value: workflowInfo?.total_workflows?.toLocaleString() ?? '0',
            },
          ]}
        />

        <MetricCard
          title='Collaboration'
          metrics={[
            {
              label: 'Shared',
              value: `${collaboration?.collaboration_percent ?? 0}%`,
            },
            {
              label: 'Count',
              value: `${collaboration?.workflows_with_collaborators ?? 0} / ${collaboration?.total_workflows ?? 0}`,
            },
          ]}
        />

        <MetricCard
          title={
            <span className='flex items-center gap-2'>
              Publication Status
              <span className='inline-flex items-center gap-1 rounded-full border border-dashed border-amber-400 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-600'>
                Coming Soon
              </span>
            </span>
          }
          metrics={[
            {
              label: 'Unpublished',
              value: `-`,
            },
            {
              label: 'Published',
              value: `-`,
            },
          ]}
        />
      </div>
    </div>
  );
}
